import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description?: string;
  path?: string;
  locale?: string;
  type?: "website" | "article";
  ogImage?: string;
  publishedDate?: string;
  updatedDate?: string;
  author?: string;
  keywords?: string;
  noindex?: boolean;
  structuredData?: object | object[];
  breadcrumbs?: { name: string; href: string }[];
  /** @deprecated No longer used - schemas are in seoData.ts */
  canonical?: string;
  /** @deprecated No longer used - schemas are in seoData.ts */
  schema?: object;
}

const DOMAIN = "https://aspab.com";

/**
 * SEOHead - Lightweight client-side title updater for SPA navigation.
 *
 * All SEO metadata (meta description, OG tags, canonical, schemas) is now
 * injected by the server from shared/seoData.ts. This component only updates
 * document.title during client-side navigation so the browser tab remains
 * correct without duplicating server-rendered metadata or structured data.
 */
export default function SEOHead({ title }: SEOHeadProps) {
  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
}

// Helper to create Organization schema
export function createOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${DOMAIN}/#organization`,
    name: "ASPAB",
    url: DOMAIN,
    logo: {
      "@type": "ImageObject",
      url: `${DOMAIN}/logo.png`,
    },
    foundingDate: "1993",
    telephone: "+46161326 00",
    email: "order@aspab.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Abramsons väg 2",
      addressLocality: "Eskilstuna",
      postalCode: "632 39",
      addressCountry: "SE",
    },
    description: "Laserskärning, kantpressning och svetsning med hög precision i Eskilstuna sedan 1993.",
    sameAs: [],
  };
}

// Helper to create WebSite schema
export function createWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ASPAB",
    url: DOMAIN,
  };
}

// Helper to create Article schema
export function createArticleSchema(opts: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    image: opts.image,
    author: {
      "@type": "Organization",
      name: "ASPAB",
    },
    publisher: {
      "@type": "Organization",
      name: "ASPAB",
      logo: {
        "@type": "ImageObject",
        url: `${DOMAIN}/logo.png`,
      },
    },
    datePublished: opts.datePublished,
    dateModified: opts.dateModified || opts.datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": opts.url,
    },
  };
}

// Helper to create FAQPage schema
export function createFAQSchema(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

// Helper to create LocalBusiness schema for geo pages
export function createLocalBusinessSchema(opts: {
  areaServed: string;
  serviceDescription: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${DOMAIN}/#localbusiness`,
    name: "ASPAB",
    url: DOMAIN,
    image: `${DOMAIN}/logo.png`,
    telephone: "+46161326 00",
    email: "order@aspab.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Abramsons väg 2",
      addressLocality: "Eskilstuna",
      postalCode: "632 39",
      addressRegion: "Södermanland",
      addressCountry: "SE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 59.3713,
      longitude: 16.5093,
    },
    description: opts.serviceDescription,
    foundingDate: "1993",
    areaServed: {
      "@type": "City",
      name: opts.areaServed,
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    makesOffer: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Laserskärning",
          description: "Professionell laserskärning med toleranser ner till 0,1 mm",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Kantpressning",
          description: "Kantpressning med tre kantpressar upp till 310 ton",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Svetsning",
          description: "Svetsning i stål, rostfritt och aluminium",
        },
      },
    ],
  };
}
